import React from 'react';
import camiyuiLogo from '../assets/camiyui-logo.jpg';

export type HomeHeroSlide = {
  id: string;
  imageUrl?: string | null;
  imagePosition?: string;
};

const FALLBACK_SLIDES: HomeHeroSlide[] = [
  {
    id: 'hero-1',
    imageUrl: camiyuiLogo,
    imagePosition: 'center 32%',
  },
  {
    id: 'hero-2',
    imageUrl: camiyuiLogo,
    imagePosition: 'center 52%',
  },
  {
    id: 'hero-3',
    imageUrl: camiyuiLogo,
    imagePosition: 'center 46%',
  },
  {
    id: 'hero-4',
    imageUrl: camiyuiLogo,
    imagePosition: 'center 28%',
  },
  {
    id: 'hero-5',
    imageUrl: camiyuiLogo,
    imagePosition: 'center 60%',
  },
];

type Props = {
  slides?: HomeHeroSlide[];
  autoPlayMs?: number;
  fullBleed?: boolean;
};

const HomeHeroCarousel: React.FC<Props> = ({
  slides = FALLBACK_SLIDES,
  autoPlayMs = 4200,
  fullBleed = false,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const touchStartXRef = React.useRef<number | null>(null);
  const touchDeltaXRef = React.useRef(0);

  const safeSlides = slides.length > 0 ? slides : FALLBACK_SLIDES;
  const frameHeightClass = fullBleed
    ? 'h-[clamp(340px,50dvh,420px)] sm:h-[clamp(400px,56dvh,540px)] lg:h-[520px]'
    : 'h-[228px] sm:h-[252px]';
  const indicatorOffsetClass = fullBleed ? 'bottom-6' : 'bottom-4';

  React.useEffect(() => {
    if (safeSlides.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % safeSlides.length);
    }, autoPlayMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [autoPlayMs, safeSlides.length]);

  const moveTo = React.useCallback((nextIndex: number) => {
    const total = safeSlides.length;
    if (total === 0) return;
    const normalized = ((nextIndex % total) + total) % total;
    setCurrentIndex(normalized);
  }, [safeSlides.length]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
    touchDeltaXRef.current = 0;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartXRef.current == null) return;
    touchDeltaXRef.current = (event.touches[0]?.clientX ?? 0) - touchStartXRef.current;
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaXRef.current) > 42) {
      moveTo(currentIndex + (touchDeltaXRef.current < 0 ? 1 : -1));
    }
    touchStartXRef.current = null;
    touchDeltaXRef.current = 0;
  };

  return (
    <section
      className={[
        'overflow-hidden bg-black',
        fullBleed
          ? 'rounded-b-[30px] border-x-0 border-t-0 border-black/10 shadow-[0_18px_40px_rgba(15,23,42,0.18)]'
          : 'rounded-[28px] border border-black/5 shadow-[0_18px_45px_rgba(15,23,42,0.16)]',
      ].join(' ')}
    >
      <div
        className={['relative touch-pan-y', frameHeightClass].join(' ')}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {safeSlides.map((slide) => (
            <div key={slide.id} className="relative h-full min-w-full overflow-hidden">
              {slide.imageUrl ? (
                <img
                  src={slide.imageUrl}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: slide.imagePosition ?? '50% 50%' }}
                />
              ) : (
                <div className="absolute inset-0 bg-slate-900" />
              )}
            </div>
          ))}
        </div>

        <div className={['absolute inset-x-0 flex justify-center gap-2', indicatorOffsetClass].join(' ')}>
          {safeSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Show slide ${index + 1}`}
              onClick={() => moveTo(index)}
              className={`h-2.5 rounded-full transition ${
                index === currentIndex ? 'w-7 bg-white' : 'w-2.5 bg-white/45'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeHeroCarousel;
